import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, Car, TrendingUp, Gauge, Users, Wrench, Sparkles } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, AreaChart, Area, BarChart, Bar, CartesianGrid
} from "recharts";
import api from "@/lib/api";
import { fadeUp } from "@/components/ui-kit";

function StatTile({ label, value, sub, icon: Icon, testId }) {
  return (
    <motion.div {...fadeUp} className="rounded-xl bg-[#1A1A1A] border border-white/10 p-5">
      <div className="flex items-start justify-between">
        <p className="text-xs tracking-[0.18em] uppercase text-[#9CA3AF]">{label}</p>
        <Icon className="w-4 h-4 text-[#3B82F6]" />
      </div>
      <p className="mt-4 font-display text-3xl md:text-4xl font-semibold text-white" data-testid={testId}>{value}</p>
      {sub && <p className="mt-1 text-xs text-[#9CA3AF]">{sub}</p>}
    </motion.div>
  );
}

const tooltipStyle = {
  background: "#0A0A0A",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 8,
  color: "#fff",
  fontSize: 12,
};

export default function Dashboard() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/dashboard/metrics").then((r) => setM(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="dashboard-page" className="min-h-screen">
      <section className="pt-28 pb-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.p {...fadeUp} className="text-xs tracking-[0.3em] uppercase text-[#3B82F6]">Fleet Dashboard · Demo</motion.p>
          <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="mt-4 font-display text-4xl md:text-6xl tracking-tighter font-semibold">
            Operations control room.
          </motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-4 max-w-2xl text-[#D1D5DB]">
            A live snapshot of GK Fleet's operations. This is a working preview of the internal tooling we're building.
          </motion.p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-6">
          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatTile testId="dash-vehicles" label="Vehicles active" value={m?.vehicles_active ?? "—"} sub={`Target ${m?.vehicles_target ?? 100}+ by 2030`} icon={Car} />
            <StatTile testId="dash-uptime" label="Operational uptime" value={`${m?.uptime_percent ?? 75}%`} sub="Rolling 30 days" icon={Gauge} />
            <StatTile testId="dash-trips" label="Trips · 30d" value={m?.trips_last_30d ?? "—"} sub="Across all platforms" icon={Activity} />
            <StatTile testId="dash-progress" label="Expansion progress" value={`${m?.expansion_progress_percent ?? "—"}%`} sub="To 2030 target" icon={TrendingUp} />
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            {/* Utilization chart */}
            <motion.div {...fadeUp} className="lg:col-span-2 rounded-xl bg-[#1A1A1A] border border-white/10 p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.18em] uppercase text-[#9CA3AF]">Monthly utilization</p>
                <Sparkles className="w-4 h-4 text-[#3B82F6]" />
              </div>
              <div className="mt-5 h-60" style={{ minHeight: 240 }}>
                <ResponsiveContainer width="100%" height={240} minHeight={240}>
                  <AreaChart data={m?.monthly_utilization || []}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis dataKey="month" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} domain={[60, 90]} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Area type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={2} fill="url(#g1)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* Vehicle status */}
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.06 }} className="rounded-xl bg-[#1A1A1A] border border-white/10 p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.18em] uppercase text-[#9CA3AF]">Vehicle status</p>
                <Wrench className="w-4 h-4 text-[#3B82F6]" />
              </div>
              <div className="mt-5 space-y-3">
                {(m?.vehicle_status_breakdown || []).map((v) => {
                  const total = m?.vehicles_active || 1;
                  const pct = Math.round((v.value / total) * 100);
                  return (
                    <div key={v.label}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#D1D5DB]">{v.label}</span>
                        <span className="text-white font-mono">{v.value}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full bg-[#3B82F6]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <motion.div {...fadeUp} className="lg:col-span-2 rounded-xl bg-[#1A1A1A] border border-white/10 p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.18em] uppercase text-[#9CA3AF]">Growth trajectory · vehicles</p>
                <TrendingUp className="w-4 h-4 text-[#3B82F6]" />
              </div>
              <div className="mt-5 h-60" style={{ minHeight: 240 }}>
                <ResponsiveContainer width="100%" height={240} minHeight={240}>
                  <BarChart data={m?.growth_trajectory || []}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis dataKey="year" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="vehicles" fill="#3B82F6" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.06 }} className="rounded-xl bg-[#1A1A1A] border border-white/10 p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.18em] uppercase text-[#9CA3AF]">Driver pipeline</p>
                <Users className="w-4 h-4 text-[#3B82F6]" />
              </div>
              <p className="mt-5 font-display text-5xl font-semibold">{m?.driver_applications_total ?? "—"}</p>
              <p className="mt-2 text-[#9CA3AF] text-sm">Total applications received</p>
              <div className="mt-6 h-px bg-white/10" />
              <p className="mt-6 text-sm text-[#9CA3AF]">Avg daily revenue / vehicle</p>
              <p className="mt-1 font-display text-2xl">₹{m?.avg_daily_revenue_per_vehicle_inr ?? "—"}</p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
